import type Graph from 'graphology';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import { NODE_COLORS } from '../lib/graph-constants.js';
import type { SigmaEdgeAttributes, SigmaNodeAttributes } from '../lib/graph-node-attributes.js';

interface RelatedNode {
  readonly id: string;
  readonly name: string;
  readonly kind: string;
}

interface RelatedGroup {
  readonly relationType: string;
  readonly nodes: RelatedNode[];
}

interface RelatedSectionProps {
  readonly graph: Graph<SigmaNodeAttributes, SigmaEdgeAttributes>;
  readonly selectedNodeId: string;
  readonly onNodeSelect: (nodeId: string) => void;
}

export function RelatedSection({
  graph,
  selectedNodeId,
  onNodeSelect,
}: RelatedSectionProps): JSX.Element {
  const groups = useMemo<RelatedGroup[]>(() => {
    const map = new Map<string, RelatedNode[]>();
    for (const edgeId of graph.edges(selectedNodeId)) {
      const relationType = graph.getEdgeAttribute(edgeId, 'relationType') ?? 'RELATED';
      const otherId =
        graph.source(edgeId) === selectedNodeId ? graph.target(edgeId) : graph.source(edgeId);
      const otherAttr = graph.getNodeAttributes(otherId);
      if (!map.has(relationType)) map.set(relationType, []);
      map.get(relationType)!.push({ id: otherId, name: otherAttr.label, kind: otherAttr.kind });
    }
    return [...map.entries()]
      .sort(([, a], [, b]) => b.length - a.length)
      .map(([relationType, nodes]) => ({ relationType, nodes }));
  }, [graph, selectedNodeId]);

  return (
    <div className="flex min-h-0 w-1/2 flex-col">
      <div className="flex items-center gap-1.5 px-4 py-1.5 text-[10px] text-text-muted">
        <span className="uppercase tracking-widest">Related ({graph.degree(selectedNodeId)})</span>
      </div>
      <div className="min-h-0 flex-1 overflow-auto px-3 pb-3">
        {groups.length === 0 && (
          <p className="px-1 py-2 font-mono text-[11px] text-text-muted">No connections.</p>
        )}
        {groups.map((g) => (
          <EdgeTypeGroup key={g.relationType} group={g} onNodeSelect={onNodeSelect} />
        ))}
      </div>
    </div>
  );
}

function EdgeTypeGroup({
  group,
  onNodeSelect,
}: {
  readonly group: RelatedGroup;
  readonly onNodeSelect: (nodeId: string) => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? group.nodes : group.nodes.slice(0, 4);

  return (
    <div className="mb-1.5">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-1 py-1 font-mono text-[10px] text-text-muted hover:text-text-secondary"
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <span className="uppercase tracking-wider">{group.relationType}</span>
        <span className="ml-auto">{group.nodes.length}</span>
      </button>
      <div className="ml-4 flex flex-col gap-0.5">
        {shown.map((n) => (
          <button
            key={n.id}
            type="button"
            onClick={() => onNodeSelect(n.id)}
            className="flex items-center gap-1.5 rounded px-1 py-0.5 text-left font-mono text-[11px] text-text-secondary transition-colors hover:bg-hover hover:text-text-primary"
          >
            <span
              className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
              style={{ background: NODE_COLORS[n.kind as keyof typeof NODE_COLORS] ?? '#6b7280' }}
            />
            <span className="truncate">{n.name}</span>
          </button>
        ))}
        {!expanded && group.nodes.length > 4 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="pl-1 text-left font-mono text-[10px] text-text-muted hover:text-text-secondary"
          >
            +{group.nodes.length - 4} more
          </button>
        )}
      </div>
    </div>
  );
}
